'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/components/AuthProvider';

export function LoginPage() {
  const { login } = useAuth();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success('Bienvenido al sistema');
      window.location.href = '/dashboard';
    } catch (err: any) {
      setError(err.message);
      toast.error(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Botón Atrás */}
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </Link>
        <div className="text-center mb-8">
          <div className="inline-flex flex-col items-center gap-4 mb-4">
            <div className="relative">
              <img
                src="/images/logo-live-moto.png"
                alt="LIVE MOTO Logo"
                className="w-48 h-auto object-contain"
              />
            </div>
            <div className="text-center">
              <h1 className="text-3xl font-bold">
                <span className="text-[#E31B23]">LIVE</span>{" "}
                <span className="text-[#C0C0C0]">MOTO</span>
              </h1>
              <p className="text-[#FF4500] font-medium mt-1">Sistema de Gestión</p>
            </div>
          </div>
          <p className="text-slate-400">Ingresa tus credenciales para continuar</p>
        </div>

        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur">
          <form onSubmit={handleSubmit}>
            <CardContent className="pt-6 space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Correo o Teléfono</Label>
                <Input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@ejemplo.com o 8095551234"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-[#E31B23] focus:ring-[#E31B23]"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Contraseña o PIN</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-[#E31B23] focus:ring-[#E31B23]"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#E31B23] to-[#FF4500] hover:from-[#FF2D35] hover:to-[#FF6B35] text-white font-semibold"
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Iniciar Sesión
              </Button>
            </CardContent>
          </form>
        </Card>

        <p className="mt-6 text-center text-slate-400 text-sm">
          ¿No tienes cuenta?{' '}
          <Link href="/register" className="text-[#FF4500] hover:text-[#FF6B35] font-semibold">
            Regístrate aquí
          </Link>
        </p>
        <p className="mt-3 text-center text-slate-500 text-xs">
          Si no recuerdas tu contraseña, solicita restablecimiento al administrador.
        </p>
      </div>
    </div>
  );
}
